import { FundingRequestStatus } from '../../funding-request/funding-request';

export enum AccBooleanValue {
  True = 'True',
  False = 'False',
}

export const ACC_BOOLEAN_DISPLAY_MAP: Record<AccBooleanValue, string> = {
  [AccBooleanValue.True]: 'Yes',
  [AccBooleanValue.False]: 'No',
};

export enum AccClaimSubmissionResponseStatus {
  Receipted = 'Receipted',
  Failed = 'Failed',
}

export enum AccAddressType {
  Home = 'Home',
  Postal = 'Postal',
}

export enum AccGender {
  Male = 'M',
  Female = 'F',
  Other = 'O',
  Unknown = 'U',
}

export enum AccDiagnosisSide {
  Left = 'left',
  Right = 'right',
  Bilateral = 'bilateral',
  NotApplicable = 'notApplicable',
}

export enum AccToothInjuryClassification {
  EnamelInfraction = 'Enamel infraction',
  EnamelFracture = 'Enamel fracture',
  EnamelDentineFracture = 'Enamel-dentine fracture',
  ComplicatedCrownFracture = 'Complicated crown fracture',
  CrownRootFracture = 'Crown root fracture',
  RootFracture = 'Root fracture',
  Concussion = 'Concussion',
  Subluxation = 'Subluxation',
  LateralLuxation = 'Lateral luxation',
  ExtrusiveLuxation = 'Extrusive luxation',
  IntrusiveLuxation = 'Intrusive luxation',
  Avulsion = 'Avulsion',
}

export enum AccToothPreAccidentCondition {
  NoPriorDamage = 'No prior damage',
  PriorDamageDueToAccInjury = 'Prior damage due to ACC injury',
  PriorDamageOtherCauses = 'Prior damage other causes',
  LittleCariesInDamagedTooth = 'Little caries in damaged tooth',
  ExtensiveCariesInDamagedTooth = 'Extensive caries in damaged tooth',
  MinimallyRestored = 'Minimally restored',
  HeavilyRestored = 'Heavily restored',
  PreviouslyCrownedOrBridged = 'Previously crowned or bridged',
  PeriodontalDiseaseWithBoneLoss = 'Periodontal disease with bone loss',
}

export enum AccProsthesisType {
  Bridge = 'Bridge',
  Crown = 'Crown',
  FullDentureMetal = 'Full denture metal',
  FullDenturePlastic = 'Full denture plastic',
  ImplantAbutment = 'Implant abutment',
  ImplantCrown = 'Implant crown',
  ImplantFixture = 'Implant fixture',
  ImplantPartialDentureMetal = 'Implant partial denture metal',
  PartialDenturePlastic = 'Partial denture plastic',
  Restoration = 'Restoration',
}

export enum AccSoftTissueHow {
  Laceration = 'Laceration',
  Abrasion = 'Abrasion',
  Contusion = 'Contusion',
}

export enum AccDeglovingInjury {
  LowerLabialSulcus = 'Lower labial sulcus',
  UpperLabialSulcus = 'Upper labial sulcus',
}

export enum AccAlveolusInjury {
  AlveolarSocket = 'Alveolar socket #',
  AlveolarProcess = 'Alveolar process #',
}

export enum AccJawInjury {
  Maxilla = 'Maxilla #',
  Mandible = 'Mandible #',
}

export enum AccTmjInjury {
  LeftSideTmjInjury = 'Left side TMJ injury',
  RightSideTmjInjury = 'Right side TMJ injury',
}

export enum AccCondition {
  Good = 'Good',
  Fair = 'Fair',
  Poor = 'Poor',
}

export enum AccCariesActivity {
  LittleOrNone = 'Little or none',
  Moderate = 'Moderate',
  Extensive = 'Extensive',
}

export enum AccIncapacityType {
  FitForSelectedWork = 'Fitforselectedwork',
  FullyUnfitForWork = 'Fullyunfitforwork',
}

export enum AccPhysicalRestriction {
  Lifting = 'Lifting',
  Posture = 'Posture',
  HeavyPhysical = 'HeavyPhysical',
  Repetition = 'Repetition',
  Vibration = 'Vibration',
  Temperature = 'Temperature',
  Driving = 'Driving',
  ProlongedSitting = 'ProlongedSitting',
  ProlongedWalking = 'ProlongedWalking',
  ProlongedStanding = 'ProlongedStanding',
  Other = 'Other',
}

export enum AccClaimDiagnosisAction {
  Add = 'add',
  Modify = 'modify',
  Delete = 'delete',
}

export enum AccClaimDiagnosisSide {
  Left = 'left',
  Right = 'right',
  Bilateral = 'bilateral',
  NotApplicable = 'notApplicable',
}

export enum AccClaimCoverStatus {
  NotApplicable = 'Not Applicable',
  InterimAccept = 'Interim Accept',
  Accept = 'Accept',
  PendingAccept = 'Pending Accept',
  AccreditedEmployer = 'Accredited Employer',
  PendingAccreditedEmp = 'Pending Accredited Emp',
  Decline = 'Decline',
  PendingDecline = 'Pending Decline',
  Duplicate = 'Duplicate',
  PendingHeld = 'Pending Held',
  Held = 'Held',
  InvestigatingGradualProcess = 'Investigating Gradual Process',
}

export const ACC_CLAIM_COVER_STATUS_DISPLAY_MAP: Record<
  AccClaimCoverStatus,
  string
> = {
  [AccClaimCoverStatus.NotApplicable]: 'Pending Acceptance',
  [AccClaimCoverStatus.InterimAccept]: 'Interim Acceptance',
  [AccClaimCoverStatus.Accept]: 'Accepted',
  [AccClaimCoverStatus.PendingAccept]: 'Pending Acceptance',
  [AccClaimCoverStatus.AccreditedEmployer]: 'Accredited Employer',
  [AccClaimCoverStatus.PendingAccreditedEmp]: 'Pending Accredited Employer',
  [AccClaimCoverStatus.Decline]: 'Declined',
  [AccClaimCoverStatus.PendingDecline]: 'Pending Decline',
  [AccClaimCoverStatus.Duplicate]: 'Duplicate',
  [AccClaimCoverStatus.PendingHeld]: 'Pending Held',
  [AccClaimCoverStatus.Held]: 'Held',
  [AccClaimCoverStatus.InvestigatingGradualProcess]:
    'Investigating Gradual Process',
};

export const ACC_CLAIM_COVER_STATUS_MAP: Record<
  AccClaimCoverStatus,
  FundingRequestStatus
> = {
  [AccClaimCoverStatus.NotApplicable]: FundingRequestStatus.Pending,
  [AccClaimCoverStatus.InterimAccept]: FundingRequestStatus.Approved,
  [AccClaimCoverStatus.Accept]: FundingRequestStatus.Approved,
  [AccClaimCoverStatus.PendingAccept]: FundingRequestStatus.Pending,
  [AccClaimCoverStatus.AccreditedEmployer]: FundingRequestStatus.Approved,
  [AccClaimCoverStatus.PendingAccreditedEmp]: FundingRequestStatus.Pending,
  [AccClaimCoverStatus.Decline]: FundingRequestStatus.Declined,
  [AccClaimCoverStatus.PendingDecline]: FundingRequestStatus.Pending,
  [AccClaimCoverStatus.Duplicate]: FundingRequestStatus.Declined,
  [AccClaimCoverStatus.PendingHeld]: FundingRequestStatus.Pending,
  [AccClaimCoverStatus.Held]: FundingRequestStatus.Pending,
  [AccClaimCoverStatus.InvestigatingGradualProcess]:
    FundingRequestStatus.Pending,
};
